// UC-3: Variant Mapping — map observed external identifiers to product Variants
//
// Pure status-filter / search / sort helpers for VariantMappingTable,
// extracted so they can be unit-tested independently of rendering
// (REST-INC3-3/4, AC-UC3-5 through AC-UC3-9, BR-UC3-3/4/6/7).

import type {
  SortDirection,
  VariantMappingRow,
  VariantMappingSortColumn,
  VariantMappingStatusView,
} from '@/shared/types/variantMapping';

export function filterRowsByStatusView(
  rows: VariantMappingRow[],
  statusView: VariantMappingStatusView,
): VariantMappingRow[] {
  if (statusView === 'all') return rows;
  return rows.filter((row) => row.status === statusView);
}

// BR-UC3-6: search covers External Identifier, Product Name, Identifier
// type, Source Channel, Ingestion transport, and Matched variant name.
// Status and Matched date are NOT free-text search targets.
export function searchVariantMappingRows(rows: VariantMappingRow[], query: string): VariantMappingRow[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return rows;

  return rows.filter((row) => {
    const haystacks = [
      row.externalIdentifier,
      row.productName,
      row.identifierType,
      row.sourceChannel,
      row.ingestionTransport,
      row.matchedVariant?.display_name ?? '',
    ];
    return haystacks.some((field) => field.toLowerCase().includes(trimmed));
  });
}

function compareValues(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export function sortVariantMappingRows(
  rows: VariantMappingRow[],
  column: VariantMappingSortColumn | null,
  direction: SortDirection,
): VariantMappingRow[] {
  if (!column) return rows;

  const sorted = [...rows].sort((a, b) => {
    switch (column) {
      case 'externalIdentifier':
        return compareValues(a.externalIdentifier, b.externalIdentifier);
      case 'productName':
        return compareValues(a.productName, b.productName);
      case 'identifierType':
        return compareValues(a.identifierType, b.identifierType);
      case 'sourceChannel':
        return compareValues(a.sourceChannel, b.sourceChannel);
      case 'ingestionTransport':
        return compareValues(a.ingestionTransport, b.ingestionTransport);
      case 'matchedVariant':
        return compareValues(a.matchedVariant?.display_name ?? '', b.matchedVariant?.display_name ?? '');
      case 'matchedDate':
        return compareValues(a.matchedVariant?.matched_at ?? '', b.matchedVariant?.matched_at ?? '');
      default:
        return 0;
    }
  });

  return direction === 'asc' ? sorted : sorted.reverse();
}

export function countRowsByStatusView(rows: VariantMappingRow[]): Record<VariantMappingStatusView, number> {
  return {
    all: rows.length,
    needs_review: rows.filter((r) => r.status === 'needs_review').length,
    unmatched: rows.filter((r) => r.status === 'unmatched').length,
    matched: rows.filter((r) => r.status === 'matched').length,
  };
}

export function formatVariantsCountLabel(input: {
  identifierTotal: number;
  variantRowTotal: number;
  unavailable: boolean;
}): string {
  if (input.unavailable) return 'Variants —';
  return `Variants ${input.identifierTotal} across ${input.variantRowTotal} sets`;
}

export const VARIANT_MAPPING_TABLE_PAGE_SIZE = 1000;

export function paginateVariantMappingRows<T>(
  rows: T[],
  page: number,
  pageSize = VARIANT_MAPPING_TABLE_PAGE_SIZE,
): { pageRows: T[]; totalPages: number; currentPage: number } {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * pageSize;
  return {
    pageRows: rows.slice(start, start + pageSize),
    totalPages,
    currentPage,
  };
}
